import React, { useCallback, useEffect, useRef, useState } from 'react'
import { SVGGroup } from '@pff-consumer/utils'
import { Text, TextDescriptionVariant } from '../text'
import { useStyles } from './toast.web.styles'
import { Icon } from '../icon/icon.web'
import { ToastProps } from './toast.props'
import { colors } from '@pff-consumer/core-ui'

export const ANIMATION_DURATION = 3000

export const Toast = (props: ToastProps) => {
  const { showToast, hasErrored, onClose } = props

  // Retain the initial error state for consistent rendering
  const hasErroredRef = useRef(hasErrored)

  const styles = useStyles({ hasErrored: hasErroredRef.current })

  const [isVisible, setIsVisible] = useState<boolean>(showToast)
  const animationClass = showToast ? styles.fadeIn : styles.fadeOut

  // Use discriminated union narrowing
  const { errorMessage, successfulMessage } = props
  const text = hasErroredRef.current ? errorMessage : successfulMessage
  const iconName = hasErroredRef.current ? 'info-white' : 'circle-checkmark-white'

  const handleClose = useCallback(() => {
    setIsVisible(false)
    onClose()
  }, [])

  useEffect(() => {
    let timer: NodeJS.Timeout
    if (showToast) {
      setIsVisible(true)

      timer = setTimeout(() => {
        onClose()
      }, ANIMATION_DURATION)
    }
    return () => {
      clearTimeout(timer)
    }
  }, [showToast, onClose])

  const handleAnimationEnd = () => {
    if (!showToast) {
      setIsVisible(false)
    }
  }

  if (!text) {
    console.warn('Toast: No message provided. Either errorMessage or successfulMessage is required.')
    return null
  }

  if (!isVisible) {
    return null
  }

  return (
    <div
      className={`${styles.toastContainer} ${animationClass}`}
      data-testid='toast'
      onAnimationEnd={handleAnimationEnd}
    >
      <div className={styles.toastSubContainer}>
        <div className={styles.toastMessageText}>
          <Icon
            name={iconName}
            group={SVGGroup.Icons}
            height={12}
            width={12}
          />
          <Text
            variant={TextDescriptionVariant.TEXT_SMALL_REGULAR}
            colorOverride={colors.lightTextColor}
            testID='toastMessage'
          >
            {text}
          </Text>
        </div>
        <button
          onClick={handleClose}
          className={styles.closeButton}
          aria-label='close'
        >
          <Icon
            name='close'
            group={SVGGroup.Icons}
            height={16}
            width={16}
          />
        </button>
        <div
          className={styles.progressBar}
          data-testid='toast.progressBar'
        />
      </div>
    </div>
  )
}
